import axios from "axios";
import { useMutation } from "react-query";
import { END_POINT } from "config/environment";
import { useGetUserInfo } from "hooks/useGetUserInfo";

interface PayInitProp {
  plan: string;
  gateway?: any;
  upgrade?: boolean;
  plan_duration?: string;
  packages?: any;
  payment_gateway_id?: number;
}

interface ProcessPaymentParams {
  planId: string;
  paymentGatewayId: number;
  planDuration: string;
  isUpgrade: boolean;
}

interface ProcessPaymentResponse {
  checkoutUrl: string;
  paymentReference?: string;
}

export const paymentInit = async (props: PayInitProp & { token: string }) => {
  const url = `${END_POINT.BASE_URL}/v1/${props.gateway?.name.toLowerCase()}-init`;
  const config = {
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${props.token}`,
    },
  };

  const data: PayInitProp = {
    plan: props.plan,
    payment_gateway_id: props.gateway?.id,
    plan_duration: props.plan_duration,
    packages: props.packages,
    upgrade: props.upgrade,
  };

  const response = await axios.post(url, data, config);
  return response;
};

export const usePaymentInit = () => {
  const { token } = useGetUserInfo();

  return useMutation((props: PayInitProp) => paymentInit({ ...props, token }));
};

export const processFlutterwavePayment = async (
  params: ProcessPaymentParams & { token: string },
): Promise<ProcessPaymentResponse> => {
  const { planId, paymentGatewayId, planDuration, isUpgrade, token } = params;

  const url = `${END_POINT.BASE_URL}/v1/flutterwave-init`;

  const config = {
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  const payload = {
    plan: planId,
    payment_gateway_id: paymentGatewayId,
    plan_duration: planDuration,
    upgrade: isUpgrade,
  };

  try {
    const response = await axios.post(url, payload, config);

    if (response.data && response.data.url) {
      return {
        checkoutUrl: response.data.url,
        paymentReference: response.data.reference || undefined,
      };
    }

    throw new Error("Invalid response from payment gateway");
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          "Payment initialization failed",
      );
    }
    throw error;
  }
};

export const useFlutterwavePayment = () => {
  const { token } = useGetUserInfo();

  return useMutation<ProcessPaymentResponse, Error, ProcessPaymentParams>(
    (params: ProcessPaymentParams) =>
      processFlutterwavePayment({ ...params, token }),
    {
      onSuccess: (data) => {
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
        }
      },
      onError: (error) => {
        console.error("Payment initialization error:", error);
      },
    },
  );
};

export const initiatePayment = async (
  props: PayInitProp & { token: string },
) => {
  if (props.gateway?.name?.toLowerCase() === "flutterwave") {
    return processFlutterwavePayment({
      planId: props.plan,
      paymentGatewayId: props.gateway.id,
      planDuration: props.plan_duration || "monthly",
      isUpgrade: props.upgrade || false,
      token: props.token,
    });
  }

  const response = await paymentInit(props);
  return response.data;
};

export const usePaymentInitUnified = () => {
  const { token } = useGetUserInfo();

  return useMutation((props: PayInitProp) =>
    initiatePayment({ ...props, token }),
  );
};
